import { NgModule } from '@angular/core';

import { TableDirective } from './directives/table.directive';
import { TableRowDirective } from './directives/table-row.directive';
import { TableSortableDirective } from './directives/table-sortable.directive';
import { TableFocusDirective } from './directives/table-focus.directive';

/**
 * Module that provides accessible table directives for sorting, selection, and keyboard navigation.
 *
 * @remarks
 * `TableModule` exports four directives that work together to create fully-featured tables:
 *
 * - {@link TableDirective} - Main directive applied to `<table>` elements; manages row selection and sorting
 * - {@link TableRowDirective} - Applied to `<tr>` elements; handles row interactivity and selection state
 * - {@link TableSortableDirective} - Applied to `<th>` headers; enables clickable sorting with keyboard support
 * - {@link TableFocusDirective} - Applied to `<table>` elements; provides keyboard navigation (arrow keys, Enter)
 *
 * All directives work independently but are designed to integrate seamlessly when used together.
 * For maximum accessibility, use all four directives in combination.
 *
 * @example
 *
 * ```html
 * <table [bbTable]="data" (sort)="onSort($event)" bbTableFocus>
 *   <thead>
 *     <tr>
 *       <th bbSortable="name" scope="col">
 *         <span class="th-content">Name</span>
 *       </th>
 *     </tr>
 *   </thead>
 *   <tbody>
 *     @for (item of data; track item.id) {
 *       <tr [bbRow]="item" (rowClick)="onRowClick($event)">
 *         <td>{{ item.name }}</td>
 *       </tr>
 *     }
 *   </tbody>
 * </table>
 * ```
 */
@NgModule({
  declarations: [TableRowDirective, TableDirective, TableSortableDirective, TableFocusDirective],
  exports: [TableRowDirective, TableDirective, TableSortableDirective, TableFocusDirective],
})
export class TableModule {}
